import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Users, Plus, Gem } from "lucide-react";
import { toast } from "sonner";

export default function Groups() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/groups")
      .then(res => setGroups(res.data))
      .catch(() => toast.error("Failed to load groups"))
      .finally(() => setLoading(false));
  }, []);

  const handleJoin = async (groupId, cost) => {
    if (cost > 0) {
      if (user.diamonds < cost) {
        toast.error("Not enough diamonds");
        return;
      }
      if (!window.confirm(`Join for ${cost} diamonds?`)) return;
    }
    try {
      await api.post(`/groups/${groupId}/join`);
      toast.success("Joined group!");
      // Refresh groups
      const res = await api.get("/groups");
      setGroups(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Join failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="font-display text-3xl font-semibold">Groups</h1>
          <Link
            to="/groups/create"
            className="neo-btn bg-purple-600 border-purple-600 text-sm"
          >
            <Plus className="w-4 h-4" /> Create Group
          </Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-purple-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : groups.length === 0 ? (
          <p className="text-center text-gray-500 py-16">No groups yet. Create the first one!</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {groups.map(group => (
              <div key={group.group_id} className="bg-white border border-[#E7E5E0] rounded-2xl p-6 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl overflow-hidden border">
                    {group.image ? (
                      <img src={group.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-[#F5F3EE] flex items-center justify-center">
                        <Users className="w-8 h-8 text-[#6B6B70]" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg truncate">{group.title}</h3>
                    <p className="text-sm text-[#6B6B70] mt-1 line-clamp-2">{group.description}</p>
                    <div className="flex items-center gap-4 mt-3 text-xs text-[#6B6B70]">
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {group.member_count} members</span>
                      {group.join_cost > 0 && (
                        <span className="flex items-center gap-1"><Gem className="w-3.5 h-3.5 text-[#3A7D44]" /> {group.join_cost} to join</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-4 flex gap-2">
                  {group.my_role ? (
                    <Link
                      to={`/groups/${group.group_id}`}
                      className="neo-btn neo-btn-secondary text-sm w-full"
                    >
                      Open
                    </Link>
                  ) : (
                    <button
                      onClick={() => handleJoin(group.group_id, group.join_cost)}
                      className="neo-btn bg-purple-600 border-purple-600 text-sm w-full"
                    >
                      {group.join_cost > 0 ? `Join for ${group.join_cost} 💎` : "Join Free"}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}